import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiskKeys } from '@/config/riskSignals';

import { Button, Loader, ModalInfo, SignalBadge } from '@/components';
import { complianceAlertLevelsActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { Direction } from '@/api';

import styles from './TransactionAlertsResetCategoryModal.module.sass';

interface TransactionAlertsResetCategoryModalProps {
  isOpen: boolean;
  direction?: Direction;
  category: RiskKeys | null;
  onReset: () => void;
  onClose: () => void;
}

const TransactionAlertsResetCategoryModal = ({
  isOpen,
  direction,
  category,
  onReset,
  onClose,
}: TransactionAlertsResetCategoryModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isResetting, setIsResetting] = useState(false);

  const { loading, error } = useAppSelector(
    (state) => state.complianceAlertLevels,
  );

  const handleOnReset = useCallback(() => {
    if (!category) return;

    setIsResetting(true);

    dispatch(
      complianceAlertLevelsActions.resetTransactionAlertsCategories({
        categories: [category],
        direction,
      }),
    );
  }, [dispatch, category, direction]);

  useEffect(() => {
    if (isResetting && !loading && !error) {
      setIsResetting(false);
      onReset();
      onClose();
    }
  }, [loading, error, isResetting, onReset, onClose]);

  if (!category) return null;

  return (
    <ModalInfo
      isOpen={isOpen}
      title={`🧹 ${t(
        'translation.alerts_settings_clear_category_modal.title',
      )}`}
      actions={
        <div className={styles.actions}>
          <Button
            size="s"
            type="secondary"
            corner="rect"
            minWidth={77}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>

          <Button
            size="s"
            type="primary"
            corner="rect"
            minWidth={120}
            onClick={handleOnReset}
          >
            {t('translation.btn_clear')}
          </Button>
        </div>
      }
      className={styles.modal}
      onClose={onClose}
    >
      <div className={styles.text}>
        {t('translation.alerts_settings_clear_category_modal.description')}{' '}
        <SignalBadge value={category} />
      </div>

      {loading && (
        <div className={styles.loader}>
          <Loader />
        </div>
      )}
    </ModalInfo>
  );
};

export default TransactionAlertsResetCategoryModal;
